// src/pages/TicketDeliveryManagement/modals/DeliveryActionConfirmModal.tsx
// Modal xác nhận Confirm/Cancel phiếu giao vé — clone pattern ApproveConfirmModal của TicketDesignTemplate

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { AgentMap, getAgentCode, getAgentName } from 'helpers/agentLookup';
import { TicketDelivery } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_delivery_management');

export type DeliveryActionType = 'confirm' | 'cancel';

interface DeliveryActionConfirmModalProps {
  isOpen: boolean;
  action: DeliveryActionType;
  delivery: TicketDelivery | null;
  agentMap: AgentMap;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
}

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '-';

const DeliveryActionConfirmModal: React.FC<DeliveryActionConfirmModalProps> = ({
  isOpen,
  action,
  delivery,
  agentMap,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const location = useLocation();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  if (!delivery) return null;

  const isConfirm = action === 'confirm';
  const actionCode = isConfirm ? 'button-confirm-submit' : 'button-cancel-submit';

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        <i
          className={`${isConfirm ? 'ri-check-double-line text-success' : 'ri-close-circle-line text-danger'} me-2`}
        ></i>
        {isConfirm ? st('xac_nhan_giao_ve_title') : st('huy_phieu_giao_ve_text')}
      </ModalHeader>
      <ModalBody>
        <Alert color={isConfirm ? 'info' : 'warning'} className="py-2">
          <i className="ri-information-line me-1"></i>
          {isConfirm
            ? st('sau_khi_xac_nhan_he_thong_text')
            : st('phieu_sau_khi_huy_se_khong_text')}
        </Alert>

        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <tbody>
              <tr>
                <td width="30%" className="fw-semibold bg-light">
                  {st('ma_phieu_column')}
                </td>
                <td>{delivery.deliveryCode}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ke_hoach_phat_hanh_column')}</td>
                <td>{delivery.planCode || delivery.issuancePlanId}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('dai_ly_nhan_text')}</td>
                <td>
                  {getAgentName(agentMap, delivery.agentId)}{' '}
                  <small className="text-muted">({getAgentCode(agentMap, delivery.agentId)})</small>
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('tong_so_ve_text')}</td>
                <td className="fw-semibold">{formatNumber(delivery.totalQuantity)}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ngay_lap_text')}</td>
                <td>{delivery.createdAt ? moment(delivery.createdAt).format('DD/MM/YYYY HH:mm') : '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {(delivery.lines || []).length > 0 && (
          <div className="table-responsive mt-3">
            <table className="table table-sm table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>{st('ky_hieu_column')}</th>
                  <th>{st('tu_so_column')}</th>
                  <th>{st('den_so_column')}</th>
                  <th className="text-end">{st('so_luong_column')}</th>
                </tr>
              </thead>
              <tbody>
                {(delivery.lines || []).map((line, index) => (
                  <tr key={line.id ?? index}>
                    <td>{line.symbol}</td>
                    <td>{line.rangeStart}</td>
                    <td>{line.rangeEnd}</td>
                    <td className="text-end">{formatNumber(line.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isConfirm && (
          <FormGroup className="mt-3 mb-0">
            <Label for="cancel-reason">{st('ly_do_huy_label')}</Label>
            <Input
              type="textarea"
              id="cancel-reason"
              rows={3}
              maxLength={500}
              placeholder={st('nhap_ly_do_huy_phieu_khong_placeholder')}
              value={reason}
              data-action-code="cancel-input-reason"
              data-action-name="Lý do huỷ phiếu giao vé"
              disabled={!PageActionPermission.canEnable(location.pathname, 'cancel-input-reason')}
              onChange={(event) => setReason(event.target.value)}
            />
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={loading}>
          {st('dong_button')}
        </Button>
        {PageActionPermission.canDisplay(location.pathname, actionCode) && (
          <Button
            color={isConfirm ? 'success' : 'danger'}
            data-action-code={actionCode}
            data-action-name={isConfirm ? st('xac_nhan_giao_ve_popup_text') : st('xac_nhan_huy_phieu_giao_ve_text')}
            disabled={loading}
            onClick={() => onConfirm(isConfirm ? undefined : reason.trim() || undefined)}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {st('dang_xu_ly_text')}
              </>
            ) : (
              <>
                <i className={`${isConfirm ? 'ri-check-line' : 'ri-close-line'} me-1`}></i>
                {isConfirm ? st('xac_nhan_giao_ve_title') : st('huy_phieu_title')}
              </>
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default DeliveryActionConfirmModal;
